import type { RouteRecordRaw } from 'vue-router';
import gettext from 'src/utils/gettext';
import {
  ApiError,
  CompanyPermissionsCodename,
  PermissionsCodename,
  VisitsApi,
} from 'src/api';
import { requireCompanyPermission } from 'src/router/guards/requireCompanyPermission';
import { requireUserPermission } from 'src/router/guards/requireUserPermission';
import { Notify } from 'quasar';

const { $gettext } = gettext;

export const patientRoutes: RouteRecordRaw[] = [
  {
    path: '',
    name: 'patients',
    component: () => import('src/pages/PatientsPage.vue'),
  },
  {
    path: 'new',
    alias: $gettext('novy'),
    name: 'new-patient',
    props: (route) => ({
      next: route.query.next,
      isFromNewVisit: route.query.isFromNewVisit,
    }),
    component: () => import('pages/patients/CreatePatientPage.vue'),
  },
  {
    path: '/waiting-patients',
    alias: $gettext('/cekajici-pacienti'),
    props: true,
    beforeEnter: requireCompanyPermission(
      CompanyPermissionsCodename.ALLOW_CALENDAR
    ),
    children: [
      {
        path: '',
        name: 'patients-wait',
        props: true,
        component: () => import('src/pages/WaitingPatientsPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('novy'),
        name: 'new-waiting-patient',
        props: true,
        component: () => import('pages/patients/CreateWaitingPatientPage.vue'),
      },
    ],
  },
  {
    path: '/visits',
    alias: $gettext('/navstevy'),
    props: true,
    children: [
      {
        path: '',
        name: 'visits',
        component: () => import('src/pages/VisitsPage.vue'),
      },
    ],
  },
  {
    path: ':patientId',
    props: true,
    children: [
      {
        path: 'patient-card',
        alias: $gettext('karta-pacienta'),
        props: true,
        children: [
          {
            path: '',
            name: 'patient-detail',
            props: (route) => ({
              patientId: route.params.patientId,
              receiptId: route.query.receiptId,
              isFromCreatePatient: route.query.isFromCreatePatient,
            }),
            component: () => import('src/pages/patients/PatientDetailPage.vue'),
          },
          {
            path: 'edit',
            alias: $gettext('upravit'),
            name: 'edit-patient',
            props: true,
            component: () => import('src/pages/patients/UpdatePatientPage.vue'),
          },
          {
            path: 'add-subscription',
            alias: $gettext('pridat-permanentku'),
            name: 'new-patient-ticket',
            props: (route) => ({
              patientId: route.params.patientId,
              next: route.query.next,
            }),
            component: () => import('pages/tickets/NewPatientTicketPage.vue'),
          },
          {
            path: 'edit-subscription/:ticketId',
            alias: $gettext('upravit-permanentku/:ticketId'),
            name: 'edit-patient-ticket',
            props: (route) => ({
              ticketId: route.params.ticketId,
              patientId: route.params.patientId,
            }),
            component: () =>
              import('src/pages/tickets/EditPatientTicketPage.vue'),
          },
        ],
      },
      {
        path: 'anamnesis',
        alias: $gettext('anamneza'),
        name: 'patient-medical-history',
        props: true,
        component: () =>
          import('src/pages/patients/PatientMedicalHistoryPage.vue'),
      },
      {
        path: 'mrecords',
        alias: $gettext('zaznamy'),
        name: 'patient-mrecords',
        props: (route) => ({
          patientId: route.params.patientId,
        }),
        component: () => import('src/pages/patients/PatientMRecordsPage.vue'),
      },
      {
        path: 'patient-visits',
        alias: $gettext('navstevy-pacienta'),
        props: true,
        children: [
          {
            path: '',
            name: 'patient-visits',
            props: true,
            component: () => import('src/pages/patients/PatientVisitsPage.vue'),
          },
          {
            path: ':visitId',
            props: true,
            beforeEnter: async (to, from, next): Promise<void> => {
              const visitId =
                to.params.visitId instanceof Array
                  ? to.params.visitId[0]
                  : to.params.visitId;
              // if the user isn't allowed to access this visit (it's not theirs, a card payment is in progress, ...),
              // BE will return a 403 which gets handled in the api client
              try {
                await VisitsApi.visitsGetVisitAccessCheckGet({
                  visitId: visitId,
                });
              } catch (err) {
                if (err instanceof ApiError && err.status === 403) {
                  Notify.create({
                    message: $gettext(
                      'Nepodařilo se načíst návštěvu pacienta:\n' +
                        err.body.detail
                    ),
                    color: 'negative',
                    icon: 'fa-solid fa-circle-exclamation',
                    timeout: 1000,
                  });
                  return next(false);
                }
              }

              return next();
            },
            children: [
              {
                path: '',
                name: 'visit-detail',
                props: (route) => ({
                  visitId: route.params.visitId,
                  patientId: route.params.patientId,
                  receiptId: route.query.receiptId,
                  // ?edit=1 opens the date/time/room editor straight away — how the calendar
                  // bubble's pencil lands on the same editor as the header's "Upravit".
                  edit: route.query.edit === '1',
                }),
                component: () =>
                  import('src/pages/patients/VisitDetailPage.vue'),
              },
              {
                path: 'pay',
                alias: $gettext('zaplatit'),
                name: 'create-payment',
                props: (route) => ({
                  visitId: route.params.visitId,
                  patientId: route.params.patientId,
                  next: route.query.next,
                  fromGroup: route.query.fromGroup === 'true',
                }),
                component: () => import('pages/patients/PaymentPage.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'photos',
        alias: $gettext('fotografie'),
        props: true,
        children: [
          {
            path: '',
            name: 'photo-list',
            props: true,
            component: () => import('src/pages/patients/PhotosListPage.vue'),
          },
          {
            path: 'comparator',
            alias: $gettext('porovnavac'),
            name: 'photo-compare',
            props: true,
            component: () =>
              import('src/pages/patients/PhotosComparerPage.vue'),
          },
        ],
      },
      {
        path: 'new',
        alias: $gettext('nova'),
        name: 'new-visit-patient',
        props: (route) => ({
          userId: route.query.userId,
          patientId: route.query.patientId,
          time: route.query.time,
          next: route.query.next,
          isFromCreatePatient: route.query.isFromCreatePatient,
          initialRoomId: route.query.initialRoomId,
        }),
        component: () => import('src/pages/VisitCreatePage.vue'),
      },
      {
        path: 'performances',
        alias: $gettext('vykony'),
        name: 'ft-tickets',
        children: [
          {
            path: '',
            name: 'ft-ticket-list',
            props: true,
            component: () =>
              import('src/pages/patients/insurance/FTTicketListPage.vue'),
          },
          {
            path: 'new-ft-voucher',
            alias: $gettext('novy-ft-poukaz'),
            name: 'new-ft-ticket',
            props: (route) => ({
              patientId: route.params.patientId,
              ftCloneId: route.query.ftCloneId,
            }),
            component: () =>
              import('src/pages/patients/insurance/CreateFtTicketPage.vue'),
          },
          {
            path: 'edit-ft-voucher/:ticketId',
            alias: $gettext('upravit-ft-poukaz/:ticketId'),
            name: 'edit-ft-ticket',
            props: (route) => ({
              ticketId: route.params.ticketId,
              patientId: route.params.patientId,
            }),
            component: () =>
              import('src/pages/patients/insurance/UpdateFtTicketPage.vue'),
          },
        ],
      },
      {
        path: 'vouchers',
        alias: $gettext('poukazy'),
        name: 'vouchers',
        children: [
          {
            path: '',
            name: 'voucher-list',
            props: true,
            component: () =>
              import('src/pages/patients/vouchers/VoucherListPage.vue'),
          },
        ],
      },
      {
        path: 'eprescriptions',
        alias: $gettext('erecept'),
        name: 'eprescriptions',
        beforeEnter: requireUserPermission(
          PermissionsCodename.CREATE_EPRESCRIPTION
        ),
        children: [
          {
            path: '',
            name: 'patient-eprescriptions',
            props: true,
            component: () =>
              import('src/pages/patients/eprescriptions/EprescriptionListPage.vue'),
          },
          {
            path: 'new',
            alias: $gettext('novy'),
            name: 'patient-eprescriptions-new',
            props: true,
            component: () =>
              import('src/pages/patients/eprescriptions/CreateEprescriptionPage.vue'),
          },
          {
            path: ':eprescriptionId/edit',
            alias: $gettext(':eprescriptionId/upravit'),
            name: 'patient-eprescriptions-edit',
            props: true,
            component: () =>
              import('src/pages/patients/eprescriptions/CreateEprescriptionPage.vue'),
          },
          {
            path: ':eprescriptionId',
            name: 'patient-eprescriptions-detail',
            props: true,
            component: () =>
              import('src/pages/patients/eprescriptions/EprescriptionDetailPage.vue'),
          },
        ],
      },
      {
        path: 'evouchers',
        alias: $gettext('epoukazy'),
        name: 'evouchers',
        beforeEnter: requireUserPermission(PermissionsCodename.CREATE_EVOUCHER),
        children: [
          {
            path: '',
            name: 'patient-evouchers',
            props: true,
            component: () =>
              import('src/pages/patients/evouchers/EvoucherListPage.vue'),
          },
          {
            path: 'new',
            alias: $gettext('novy'),
            name: 'patient-evouchers-new',
            props: true,
            component: () =>
              import('src/pages/patients/evouchers/CreateEvoucherPage.vue'),
          },
          {
            path: ':evoucherId/edit',
            alias: $gettext(':evoucherId/upravit'),
            name: 'patient-evouchers-edit',
            props: true,
            component: () =>
              import('src/pages/patients/evouchers/CreateEvoucherPage.vue'),
          },
          {
            path: ':evoucherId',
            name: 'patient-evouchers-detail',
            props: true,
            component: () =>
              import('src/pages/patients/evouchers/EvoucherDetailPage.vue'),
          },
        ],
      },
      {
        path: 'esick-notes',
        alias: $gettext('eneschopenky'),
        name: 'esick-notes',
        beforeEnter: requireUserPermission(
          PermissionsCodename.CREATE_ESICK_NOTE
        ),
        children: [
          {
            path: '',
            name: 'patient-esick-notes',
            props: true,
            component: () =>
              import('src/pages/patients/esick-notes/ESickNoteListPage.vue'),
          },
          {
            path: 'new',
            alias: $gettext('nova'),
            name: 'patient-esick-notes-new',
            props: true,
            component: () =>
              import('src/pages/patients/esick-notes/ESickNoteFilingPage.vue'),
          },
          {
            path: ':batch/:caseId',
            name: 'patient-esick-notes-detail',
            props: true,
            component: () =>
              import('src/pages/patients/esick-notes/ESickNoteDetailPage.vue'),
          },
        ],
      },
    ],
  },
  {
    path: ':patientId/visit-descriptions',
    alias: $gettext(':patientId/popisy-navstev'),
    name: 'visit-descriptions',
    props: true,
    component: () =>
      import('src/pages/patients/PatientVisitDescriptionsPage.vue'),
  },
];

// Klienti → web `/patients` (`/pacienti`). Nested segments mirror web so paths
// and Czech aliases compose identically. `patients` / `patient-detail` /
// `new-patient` / `new-waiting-patient` reuse web names (params match).
export const mobilePatientRoutes: RouteRecordRaw[] = [
  {
    path: 'patients',
    alias: $gettext('pacienti'),
    children: [
      {
        path: '',
        name: 'patients',
        meta: { bottomNav: true },
        beforeEnter: requireUserPermission(
          PermissionsCodename.VIEW_PATIENTS_TAB
        ),
        component: () => import('pages/mobile/MobileClientsPage.vue'),
      },
      {
        path: 'new',
        alias: $gettext('novy'),
        name: 'new-patient',
        beforeEnter: requireUserPermission(PermissionsCodename.CREATE_PATIENT),
        component: () => import('pages/mobile/MobileNewClientPage.vue'),
      },
      {
        path: ':patientId/patient-card',
        alias: ':patientId/' + $gettext('karta-pacienta'),
        name: 'patient-detail',
        props: true,
        meta: { bottomNav: true },
        beforeEnter: requireUserPermission(
          PermissionsCodename.GET_PATIENT_DETAIL
        ),
        component: () => import('pages/mobile/MobileClientDetailPage.vue'),
      },
      {
        // Mobile-only leaf (web anamnesis has no dedicated edit route).
        path: ':patientId/anamnesis/edit',
        alias: ':patientId/' + $gettext('anamneza') + '/' + $gettext('upravit'),
        name: 'patient-medical-history-edit',
        props: true,
        beforeEnter: requireUserPermission(
          PermissionsCodename.UPDATE_MEDICAL_HISTORY
        ),
        component: () =>
          import('pages/mobile/MobileMedicalHistoryEditPage.vue'),
      },
    ],
  },
  {
    path: 'waiting-patients',
    alias: $gettext('cekajici-pacienti'),
    children: [
      {
        path: 'new',
        alias: $gettext('novy'),
        name: 'new-waiting-patient',
        // Same gates as the web "Čekající" subtab (PatientListNavBar.vue).
        beforeEnter: [
          requireUserPermission(
            PermissionsCodename.CREATE_PATIENT,
            PermissionsCodename.VIEW_PATIENTS_WAITING_SUBTAB
          ),
          requireCompanyPermission(
            CompanyPermissionsCodename.CREATE_NEXT_PATIENT
          ),
          requireCompanyPermission(CompanyPermissionsCodename.ALLOW_CALENDAR),
        ],
        component: () => import('pages/mobile/MobileNewWaitingClientPage.vue'),
      },
    ],
  },
];
